import { ReactNode } from 'react'
import { YStack, XStack, Text, Image, View, useMedia } from 'tamagui'
import { useTranslation } from 'react-i18next'
import topBackground from '@assets/product/top_background.png'
import { IProductPage } from '@dtos/ProductPage'
import { CONTAINER_WIDTH } from './constants'
import { ctaKind } from './helpers'
import { usePricing } from './hooks'
import { BuyButton } from './BuyButton'
import { CoverMedia } from './CoverMedia'
import { SchoolLogo } from './SchoolLogo'
import { AnchorPrice, CurrentPrice, InstallmentLine } from './PriceParts'

function HeroTitle({ product }: { product: IProductPage }) {
  const media = useMedia()

  return (
    <YStack gap={12}>
      <Text
        tag="h1"
        color="$text-primary"
        fontSize={media.gtSm ? 48 : 32}
        lineHeight={media.gtSm ? 56 : 40}
        fontFamily="$mulishBold"
        fontWeight="700"
        textAlign={media.gtSm ? 'left' : 'center'}
      >
        {product.title}
      </Text>
      {product.subtitle ? (
        <Text
          color="$text-primary"
          fontSize={20}
          fontFamily="$mulishSemiBold"
          fontWeight="600"
          textAlign={media.gtSm ? 'left' : 'center'}
        >
          {product.subtitle}
        </Text>
      ) : null}
    </YStack>
  )
}

function HeroPayments({ product }: { product: IProductPage }) {
  const { t } = useTranslation('ProductPage')
  const media = useMedia()
  const {
    option,
    isFree,
    period,
    price,
    anchorPrice,
    installment,
    installmentKey,
  } = usePricing(product)
  const align = media.gtSm ? 'flex-start' : 'center'

  return (
    <YStack gap={24} alignItems={align}>
      {option && !isFree && (
        <YStack gap={8} alignItems={align} testID="product-price">
          <Text color="$text-primary" fontSize={16} fontFamily="$mulishRegular">
            {t('price.prefix')}
          </Text>
          <XStack
            alignItems="baseline"
            gap={8}
            flexWrap="wrap"
            justifyContent={align}
          >
            {anchorPrice && <AnchorPrice value={anchorPrice} fontSize={16} />}
            <CurrentPrice
              price={price}
              period={period}
              color="$cta"
              fontSize={32}
              periodFontSize={16}
            />
          </XStack>
          {installment && (
            <InstallmentLine
              installment={installment}
              installmentKey={installmentKey}
              color="$accent"
              fontSize={16}
            />
          )}
        </YStack>
      )}

      <BuyButton
        label={t(`cta.${ctaKind(option, product.course?.kind ?? null)}`)}
        url={option?.checkoutUrl ?? null}
        location="hero"
        product={product}
      />
    </YStack>
  )
}

// Faixa superior (productPage_top): título + preço + CTA à esquerda,
// capa/vídeo à direita, fundo contrast com o top_background.png.
export function HeroSection({ product }: { product: IProductPage }) {
  const media = useMedia()

  const details: ReactNode = <HeroTitle product={product} />
  const payments: ReactNode = <HeroPayments product={product} />
  const cover: ReactNode = (
    <CoverMedia
      videoCover={product.videoCover}
      imageUrl={product.logo}
      title={product.title}
    />
  )

  return (
    <View width="100%" backgroundColor="$bg-surface" position="relative">
      {media.gtSm && (
        <Image
          source={topBackground}
          alt=""
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          width="45%"
          height="100%"
          resizeMode="contain"
          opacity={0.6}
          pointerEvents="none"
        />
      )}

      <YStack
        width="100%"
        maxWidth={CONTAINER_WIDTH}
        alignSelf="center"
        paddingVertical={64}
        paddingHorizontal={16}
      >
        <SchoolLogo centered={!media.gtSm} />

        {/* Mobile segue a grid do hero_theme: details → media → payments. */}
        {media.gtSm ? (
          <XStack width="100%" gap={48} alignItems="flex-start">
            <YStack flex={1} alignItems="flex-start" gap={24}>
              {details}
              {payments}
            </YStack>
            {cover}
          </XStack>
        ) : (
          <YStack width="100%" gap={32} alignItems="center">
            {details}
            {cover}
            {payments}
          </YStack>
        )}
      </YStack>
    </View>
  )
}
